/**
 * Minimal `.env` blob parser/serializer for bulk import/export in the
 * Environment tab. Supports:
 *   - KEY=value
 *   - KEY="quoted value with spaces"
 *   - KEY='single-quoted'
 *   - blank lines and `# comment` lines (ignored)
 *   - escaped quotes inside double-quoted values (\" → ")
 *
 * Out of scope: variable interpolation (${OTHER}), multi-line values,
 * shell-style backslash continuations.
 */

export interface ParsedEnvEntry {
	key: string;
	value: string;
}

export interface ParseEnvError {
	line: number;
	message: string;
}

export interface ParseEnvResult {
	entries: ParsedEnvEntry[];
	errors: ParseEnvError[];
}

const KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function parseEnvBlob(blob: string): ParseEnvResult {
	const entries: ParsedEnvEntry[] = [];
	const errors: ParseEnvError[] = [];
	const seenKeys = new Set<string>();

	const lines = blob.split(/\r?\n/);
	for (let i = 0; i < lines.length; i++) {
		const raw = lines[i] ?? '';
		const lineNumber = i + 1;
		const trimmed = raw.trim();
		if (trimmed === '' || trimmed.startsWith('#')) continue;

		// Allow optional `export` prefix
		const noExport = trimmed.replace(/^export\s+/i, '');
		const eqIdx = noExport.indexOf('=');
		if (eqIdx === -1) {
			errors.push({ line: lineNumber, message: 'missing "=" separator' });
			continue;
		}

		const key = noExport.slice(0, eqIdx).trim();
		const rawValue = noExport.slice(eqIdx + 1);

		if (!KEY_PATTERN.test(key)) {
			errors.push({ line: lineNumber, message: `invalid key "${key}"` });
			continue;
		}

		const value = unquoteValue(rawValue);
		if (seenKeys.has(key)) {
			// Last-write-wins, but flag as a soft warning.
			const idx = entries.findIndex((e) => e.key === key);
			if (idx >= 0) entries[idx] = { key, value };
			errors.push({ line: lineNumber, message: `duplicate key "${key}" overrides earlier value` });
		} else {
			entries.push({ key, value });
			seenKeys.add(key);
		}
	}

	return { entries, errors };
}

function unquoteValue(raw: string): string {
	let v = raw.trim();
	// Strip trailing inline comment if value isn't quoted
	if (!v.startsWith('"') && !v.startsWith("'")) {
		const hashIdx = v.indexOf(' #');
		if (hashIdx >= 0) v = v.slice(0, hashIdx).trim();
		return v;
	}
	const quote = v[0];
	if (v.length >= 2 && v.endsWith(quote)) {
		v = v.slice(1, -1);
		if (quote === '"') {
			v = v.replace(/\\"/g, '"').replace(/\\n/g, '\n').replace(/\\\\/g, '\\');
		}
	}
	return v;
}

/** Serialize entries back to canonical `.env` form. Values containing
 *  whitespace, `#`, or quotes are double-quoted with `"` escaped. */
export function serializeEnvBlob(entries: readonly ParsedEnvEntry[]): string {
	return (
		entries
			.map(({ key, value }) => {
				const needsQuote = /[\s#"'\\]/.test(value) || value === '';
				if (!needsQuote) return `${key}=${value}`;
				const escaped = value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
				return `${key}="${escaped}"`;
			})
			.join('\n') + '\n'
	);
}

/** Compute added / changed / removed keys between current saved set and
 *  proposed entries. Treats missing-from-proposed as "removed". */
export function diffEntries(
	current: readonly { key: string; value: string }[],
	proposed: readonly { key: string; value: string }[]
): { added: string[]; changed: string[]; removed: string[] } {
	const currentMap = new Map(current.map((e) => [e.key, e.value]));
	const proposedMap = new Map(proposed.map((e) => [e.key, e.value]));
	const added: string[] = [];
	const changed: string[] = [];
	const removed: string[] = [];
	for (const [k, v] of proposedMap.entries()) {
		if (!currentMap.has(k)) added.push(k);
		else if (currentMap.get(k) !== v) changed.push(k);
	}
	for (const k of currentMap.keys()) {
		if (!proposedMap.has(k)) removed.push(k);
	}
	return { added, changed, removed };
}
